import React, { useState, useEffect } from 'react';

interface NewsItem {
  id: string;
  title: string;
  date: string;
  summary: string;
  category: string;
  website: string;
  link?: string;
  image?: string;
  created_at: string;
  updated_at: string;
}

interface NewsSectionProps {
  website?: string;
  limit?: number;
  title?: string;
  themeColor?: 'purple' | 'blue' | 'green' | 'emerald' | 'indigo' | 'cyan' | 'red';
}

const NewsSection: React.FC<NewsSectionProps> = ({
  website = 'all',
  limit = 3,
  title = 'Travel News & Updates',
  themeColor = 'purple'
}) => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Theme colors mapping
  const themeColors = {
    purple: {
      bg: 'bg-purple-50',
      text: 'text-purple-800',
      accent: 'text-purple-600',
      border: 'border-purple-200',
      button: 'bg-purple-600 hover:bg-purple-700',
      badge: 'bg-purple-100 text-purple-800'
    },
    blue: {
      bg: 'bg-blue-50',
      text: 'text-blue-800',
      accent: 'text-blue-600',
      border: 'border-blue-200',
      button: 'bg-blue-600 hover:bg-blue-700',
      badge: 'bg-blue-100 text-blue-800'
    },
    green: {
      bg: 'bg-green-50',
      text: 'text-green-800',
      accent: 'text-green-600',
      border: 'border-green-200',
      button: 'bg-green-600 hover:bg-green-700',
      badge: 'bg-green-100 text-green-800'
    },
    emerald: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-800',
      accent: 'text-emerald-600',
      border: 'border-emerald-200',
      button: 'bg-emerald-600 hover:bg-emerald-700',
      badge: 'bg-emerald-100 text-emerald-800'
    },
    indigo: {
      bg: 'bg-indigo-50',
      text: 'text-indigo-800',
      accent: 'text-indigo-600',
      border: 'border-indigo-200',
      button: 'bg-indigo-600 hover:bg-indigo-700',
      badge: 'bg-indigo-100 text-indigo-800'
    },
    cyan: {
      bg: 'bg-cyan-50',
      text: 'text-cyan-800',
      accent: 'text-cyan-600',
      border: 'border-cyan-200',
      button: 'bg-cyan-600 hover:bg-cyan-700',
      badge: 'bg-cyan-100 text-cyan-800'
    },
    red: {
      bg: 'bg-red-50',
      text: 'text-red-800',
      accent: 'text-red-600',
      border: 'border-red-200',
      button: 'bg-red-600 hover:bg-red-700',
      badge: 'bg-red-100 text-red-800'
    }
  };

  const colors = themeColors[themeColor];

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get category icon
  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      'announcement': '📢',
      'retreat': '🧘',
      'event': '🎉',
      'website-update': '🔄',
      'website-launch': '🚀',
      'martial-arts': '🥋',
      'travel': '🧭',
      'culture': '☯️',
      'tourism': '🏔️'
    };
    return icons[category] || '';
  };

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        
        // Generate cache key based on website and limit
        const cacheKey = `news_cache_${website}_${limit}`;
        const cacheTimestampKey = `${cacheKey}_timestamp`;
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
        
        // Check for cached data
        const cachedData = localStorage.getItem(cacheKey);
        const cachedTimestamp = localStorage.getItem(cacheTimestampKey);
        
        // If we have valid cached data (less than 5 minutes old), use it immediately
        if (cachedData && cachedTimestamp) {
          const cacheAge = Date.now() - parseInt(cachedTimestamp);
          if (cacheAge < CACHE_DURATION) {
            try {
              const parsedData = JSON.parse(cachedData);
              if (Array.isArray(parsedData) && parsedData.length > 0) {
                setNews(parsedData);
                // Set loading to false temporarily while we fetch fresh data in background
                setLoading(false);
              }
            } catch (e) {
              console.warn('Failed to parse cached news:', e);
            }
          }
        }
        
        // Always fetch fresh data in background
        const apiUrl = `https://wudang-news-api.lomashongkong.workers.dev/api/news?website=${website}&limit=${limit}`;
        
        // Add a timeout to the fetch request
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        try {
          const response = await fetch(apiUrl, { signal: controller.signal });
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch news: ${response.status}`);
          }
          
          const data = await response.json();
          
          if (data.success && data.data) {
            // Update state with fresh data
            setNews(data.data);
            
            // Cache the fresh data
            try {
              localStorage.setItem(cacheKey, JSON.stringify(data.data));
              localStorage.setItem(cacheTimestampKey, Date.now().toString());
            } catch (e) {
              console.warn('Failed to cache news data:', e);
            }
          } else {
            throw new Error('Invalid response format');
          }
        } catch (fetchError) {
          console.error('Error fetching fresh news:', fetchError);
          
          // Only set error if we don't have cached data
          if (!cachedData || !cachedTimestamp || 
              Date.now() - parseInt(cachedTimestamp) >= CACHE_DURATION) {
            setError(fetchError instanceof Error ? fetchError.message : 'Failed to load news');
            // Fallback to sample data only if no valid cache
            setNews(getSampleNews());
          }
        }
      } catch (err) {
        console.error('Error in fetchNews:', err);
        setError(err instanceof Error ? err.message : 'Failed to load news');
        // Fallback to sample data
        setNews(getSampleNews());
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [website, limit]);

  // Sample data for fallback
  const getSampleNews = (): NewsItem[] => {
    return [
      {
        id: 'travel_news_1',
        title: 'Solo Travel Updates',
        date: '2026-04-06',
        summary: 'Latest travel information and updates for global travel community.',
        category: 'travel',
        website: 'holagoto',
        link: 'https://holagoto.com',
        created_at: '2026-04-06T12:00:00Z',
        updated_at: '2026-04-06T12:00:00Z'
      },
      {
        id: 'travel_news_2',
        title: 'Wudang Network News',
        date: '2026-04-06',
        summary: 'Stay updated with news from the Wudang network of websites.',
        category: 'announcement',
        website: 'all',
        link: 'https://wudanghub.com',
        created_at: '2026-04-06T12:00:00Z',
        updated_at: '2026-04-06T12:00:00Z'
      }
    ];
  };

  // Extract image URLs from HTML summary (up to maxCount)
  const extractThumbnails = (html: string, maxCount = 3): string[] => {
    const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
    const urls: string[] = [];
    let match;
    while ((match = imgRegex.exec(html)) !== null && urls.length < maxCount) {
      urls.push(match[1]);
    }
    return urls;
  };

  // Strip HTML tags and get plain text
  const stripHtml = (html: string): string => {
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim();
  };

  // Get first N lines of plain text (for preview)
  const getTextPreview = (html: string, maxLines = 3): string => {
    const text = stripHtml(html);
    const lines = text.split('\n');
    return lines.slice(0, maxLines).join('\n');
  };

  // Ensure summary is proper HTML (wraps plain text in <p> tags if needed)
  const ensureHtmlFormat = (html: string): string => {
    // If the content is already HTML (starts with a tag), return as-is
    if (/^\s*</.test(html)) {
      return html;
    }
    // Plain text - preserve line breaks by converting to HTML
    // Double newlines = paragraph breaks
    return html
      .split(/\n{2,}/)
      .map(para => {
        const trimmed = para.trim();
        if (!trimmed) return '';
        // Single newlines within a paragraph become <br>
        const lines = trimmed.split('\n');
        return '<p>' + lines.join('<br />') + '</p>';
      })
      .filter(p => p !== '<p></p>')
      .join('\n');
  };

  // Toggle expand/collapse state for a news item
  const toggleExpand = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // Get three image thumbnails
  const getThumbnails = (html: string, maxCount = 3): string[] => {
    return extractThumbnails(html, maxCount);
  };
  
  // Show cached data while loading fresh data
  // Sort news by date (newest first) to ensure latest news appears first
  const displayNews = (news.length > 0 ? news : getSampleNews())
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  
  // Calculate pagination
  const totalItems = displayNews.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  // Ensure currentPage is within valid range
  const validPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (validPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedNews = displayNews.slice(startIndex, endIndex);
  
  // If currentPage is out of range, adjust it (but only if totalItems > 0)
  if (currentPage !== validPage && totalItems > 0) {
    setCurrentPage(validPage);
  }

  if (loading && displayNews.length === 0) {
    return (
      <section className={`py-12 ${colors.bg}`}>
        <div className="container mx-auto px-4">
          <h2 className={`text-3xl font-bold mb-8 ${colors.text}`}>{title}</h2>
          <div className="text-center py-8">
            <div className="inline-block w-12 h-12 border-4 border-t-4 border-gray-300 border-t-purple-600 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Loading latest news...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error && displayNews.length === 0) {
    return (
      <section className={`py-12 ${colors.bg}`}>
        <div className="container mx-auto px-4">
          <h2 className={`text-3xl font-bold mb-8 ${colors.text}`}>{title}</h2>
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">Unable to load news. Please check your connection.</p>
            <button 
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-12 ${colors.bg}`} id="news">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h2 className={`text-3xl font-bold ${colors.text}`}>{title}</h2>
            <p className="text-gray-600 mt-2">
              Stay updated with the latest travel news and Wudang network updates
            </p>
          </div>
          
          {/* View All button removed as per requirements */}
        </div>
        
        <div className="grid grid-cols-1 gap-6">
          {paginatedNews.map((item) => (
            <div 
              key={item.id} 
              className={`bg-white rounded-xl shadow-lg p-6 border ${colors.border} hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}
            >
              <div className="flex justify-between items-center mb-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colors.badge}`}>
                  {getCategoryIcon(item.category)} {item.category}
                </span>
                <span className="text-sm text-gray-500">{formatDate(item.date)}</span>
              </div>
              
              <h3 className={`text-xl font-bold mb-3 ${colors.text}`}>{item.title}</h3>
              
              <div className="mb-4">
                {/* Thumbnails (visible in both states, in expanded state shown as part of full HTML) */}
                {!expandedItems[item.id] && (
                  (() => {
                    const thumbnails = getThumbnails(item.summary);
                    return thumbnails.length > 0 ? (
                      <div className="flex flex-wrap gap-3 mb-3">
                        {thumbnails.map((url, i) => (
                          <img
                            key={i}
                            src={url}
                            alt={`Thumbnail ${i + 1}`}
                            className="w-32 h-24 object-cover rounded-lg border border-gray-200 shadow-sm"
                            loading="lazy"
                          />
                        ))}
                      </div>
                    ) : null;
                  })()
                )}

                {/* Preview text (first 3 lines, visible when collapsed) */}
                {!expandedItems[item.id] && stripHtml(item.summary).length > 0 && (
                  <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                    {getTextPreview(item.summary, 3)}
                  </div>
                )}

                {/* Full content (visible when expanded) */}
                {expandedItems[item.id] && (
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: ensureHtmlFormat(item.summary) }}>
                  </div>
                )}
              </div>
              
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  {item.link && (
                    <a 
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`font-medium ${colors.accent} hover:underline`}
                    >
                      Read More →
                    </a>
                  )}
                </div>
                <div>
                  {/* Show More / Show Less button - fixed at bottom-right */}
                  {item.summary && stripHtml(item.summary).length > 0 && (
                    <button 
                      className={`${colors.accent} hover:opacity-80 text-sm font-medium flex items-center gap-1`}
                      onClick={() => toggleExpand(item.id)}
                    >
                      {expandedItems[item.id] ? (
                        <>
                          <span>Show Less</span>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 15L12 9L18 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </>
                      ) : (
                        <>
                          <span>Show More</span>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-6 mt-10 pt-8 border-t border-gray-200">
            <button
              className={`px-4 py-2 rounded-lg border ${colors.border} ${colors.text} hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={validPage === 1}
            >
              ← Previous
            </button>
            <div className={`text-sm ${colors.text}`}>
              Page {validPage} of {totalPages} ({totalItems} total news)
            </div>
            <button
              className={`px-4 py-2 rounded-lg border ${colors.border} ${colors.text} hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={validPage === totalPages}
            >
              Next →
            </button>
          </div>
        )}

        <div className="mt-10 pt-8 border-t border-gray-200 text-center">
          <p className="text-gray-600 mb-4">
            News updated daily from the Wudang Network
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button 
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors flex items-center"
              onClick={() => window.location.reload()}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh News
            </button>
            <a 
              href="mailto:info@holagoto.com?subject=News Suggestion"
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Suggest News
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;